import { useMemo, useRef, useState } from 'react';
import { Illustration } from '../../components/Illustration';
import { cn } from '../../lib/utils';
import { hireDaysToTarget, incomeByCategory, paidBackPercent } from '../../lib/equipmentInsights';
import { getCategoryColor, ON_CATEGORY_COLOR } from '../../utils/categoryColors';
import { PaidBackBar } from './PaidBack';
import { prefersReducedMotion, useCountUp, usePlayOnView } from './motion';
import { gearArt, shortName } from './gear';

// The homepage's pictures of the numbers: drawings and a figure or two each,
// not the app's own screens. Every figure comes from the template's kit.

function useReveal(threshold = 0.5) {
  const ref = useRef(null);
  const seen = usePlayOnView(ref, threshold);
  const [instant] = useState(prefersReducedMotion);
  return { ref, run: seen, instant };
}

// One piece of gear as a row: its drawing and name, how much of its cost it
// has earned back, and that as a bar. `inverse` for the dark tile.
function GearRow({ item, run, instant, inverse = false }) {
  const target = paidBackPercent(item.total_income, item.purchase_price);
  const percent = useCountUp(0, target, run, { ms: 1200, instant });
  return (
    <li className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <Illustration name={gearArt(item.name)} inverse={inverse} className="w-12 shrink-0 aspect-square" />
        <span className="flex-1 min-w-0 truncate">{shortName(item.name)}</span>
        <span className="text-2xl font-light tabular-nums">{Math.round(percent)}%</span>
      </div>
      <PaidBackBar percent={percent} inverse={inverse} />
    </li>
  );
}

// One tile of the insights: a short title, the picture, one line of fact
function Tile({ title, fact, inverse = false, className, children }) {
  return (
    <div
      className={cn(
        'p-6 sm:p-7 flex flex-col gap-6 min-w-0',
        inverse ? 'bg-inverse text-inverse-foreground rounded-highlight' : 'bg-surface rounded-tile',
        className,
      )}
    >
      <h3 className="text-2xl font-normal leading-tight [text-wrap:balance]">{title}</h3>
      {children}
      <p className={cn('mt-auto text-[15px] leading-snug', inverse ? 'text-inverse-muted' : 'text-muted-foreground')}>{fact}</p>
    </div>
  );
}

// Each category's share of the kit's income, as one bar
export function CategoryShare({ equipment }) {
  const { ref, run, instant } = useReveal();
  const categories = useMemo(() => incomeByCategory(equipment), [equipment]);
  const named = categories.filter((c) => c.share >= 7);
  return (
    <div ref={ref} className="flex flex-col gap-5">
      <div className="flex h-12 rounded-tile overflow-hidden" role="img" aria-label="Income by category">
        {categories.map((c, i) => (
          <div
            key={c.name}
            className={cn('h-full flex items-end p-3 min-w-[4px]', !instant && 'transition-[flex-grow] duration-1000 ease-out')}
            style={{
              flexGrow: run ? c.share : 0,
              flexBasis: 0,
              backgroundColor: getCategoryColor(c.name),
              color: ON_CATEGORY_COLOR,
              transitionDelay: `${i * 60}ms`,
            }}
          >
            {c.share >= 12 && <span className="text-sm whitespace-nowrap">{Math.round(c.share)}%</span>}
          </div>
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-6 gap-y-2 text-[15px]">
        {[...named, ...categories.filter((c) => c.name === 'Lighting')].map((c) => (
          <li key={c.name} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: getCategoryColor(c.name) }} aria-hidden="true" />
            {c.name}
            <span className="text-muted-foreground">{Math.round(c.share)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const TARGETS = [
  [100, 'Pay for itself'],
  [200, 'Double its money'],
];

// A kit's hire days to a target, one dot a day
function PaybackDots({ kit }) {
  const [target, setTarget] = useState(200);
  const days = hireDaysToTarget({
    cost: kit.total_purchase_price,
    income: kit.total_income,
    dayRate: kit.total_daily_rate,
    targetPercent: target,
  });
  return (
    <div className="flex flex-col gap-5">
      <div className="inline-flex self-start rounded-pill bg-mist p-1" role="group" aria-label="Target">
        {TARGETS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setTarget(value)}
            aria-pressed={target === value}
            className={cn(
              'rounded-pill px-3.5 py-1.5 text-sm transition-colors duration-200',
              target === value ? 'bg-inverse text-inverse-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="flex items-baseline gap-3">
        <span className="text-6xl font-light tracking-tight leading-none tabular-nums">{days}</span>
        <span className="text-muted-foreground">hire {days === 1 ? 'day' : 'days'} to go</span>
      </p>
      <div className="flex flex-wrap gap-1" aria-hidden="true">
        {Array.from({ length: days }, (_, i) => (
          <span key={i} className="w-2 h-2 rounded-full bg-foreground" />
        ))}
      </div>
    </div>
  );
}

// What's working and what isn't, as four tiles: the most profitable piece
// against the priciest, two lights that earned the same, where the income
// comes from, and how many hire days a kit has to go
export function GearInsights({ story, equipment, kit, money, percent }) {
  const { ref, run, instant } = useReveal(0.3);
  const { mvp, priciest, lights } = story;
  return (
    <div ref={ref} className="grid md:grid-cols-2 gap-4 lg:gap-6">
      <Tile
        inverse
        title={<>Not the priciest.<br /> The most profitable.</>}
        fact={`A ${money(mvp.purchase_price)} ${shortName(mvp.name)} has out-earned its cost. The ${money(priciest.purchase_price)} ${shortName(priciest.name)} hasn’t yet.`}
      >
        <ul className="flex flex-col gap-5">
          <GearRow item={mvp} run={run} instant={instant} inverse />
          <GearRow item={priciest} run={run} instant={instant} inverse />
        </ul>
      </Tile>
      <Tile
        title={<>Same work.<br /> Twice the price.</>}
        fact={`${lights.dear.total_days} days on hire each, ${money(lights.dear.total_income)} earned each. One cost twice as much.`}
      >
        <ul className="flex flex-col gap-5">
          <GearRow item={lights.cheap} run={run} instant={instant} />
          <GearRow item={lights.dear} run={run} instant={instant} />
        </ul>
      </Tile>
      <Tile
        title="Know where the value lives."
        fact={`Camera brings in ${percent(story.cameraShare)} of the income. Lighting, ${percent(story.lightingShare)}.`}
      >
        <CategoryShare equipment={equipment} />
      </Tile>
      {kit && (
        <Tile title={<>Name the target.<br /> Count the days.</>} fact={kit.name}>
          <PaybackDots kit={kit} />
        </Tile>
      )}
    </div>
  );
}
